import { Box } from '../../render/parts'
import { BookStack } from '../parts/props'
import { BinderRow } from '../parts/storage'
import type { AssetContext, AssetDefinition } from '../types'

const WIDTH = 0.9
const DEPTH = 0.35
const TALL = 1.6
const SHORT = 1.1

const THICK = 0.022
/** Toe recess: the sides run to the floor, the bottom board sits above it. */
const TOE = 0.06
/** Boards bite 2 mm into each side panel so no two faces are coplanar. */
const BOARD_WIDTH = WIDTH - THICK * 2 + 0.004
/** ...and are held back 6 mm from the front and back edges of the sides. */
const BOARD_DEPTH = DEPTH - 0.012

/**
 * Board centre heights, bottom board first, top board last. The bays come out
 * equal, which is what makes an open frame read as a single clean grid.
 */
function boardLevels(height: number, bays: number) {
  const pitch = (height - THICK - TOE) / bays
  return Array.from({ length: bays + 1 }, (_, index) => TOE + THICK / 2 + index * pitch)
}

/**
 * Open shelving unit, 0.9 x 0.35, 1.6 or 1.1 m tall. Origin at the centre of the
 * footprint, y = 0 on the floor, open face towards +Z.
 *
 * Built from raw boxes rather than `Carcass` on purpose: this unit has no back
 * panel, so the wall shows straight through it.
 */
function ShelfOpen({ accent, variant = 'tall' }: AssetContext) {
  const height = variant === 'short' ? SHORT : TALL
  const levels = boardLevels(height, variant === 'short' ? 3 : 4)

  return (
    <group>
      {/* Side panels, full height, floor to top. */}
      {[-1, 1].map((side) => (
        <Box
          key={side}
          tone="shell"
          size={[THICK, height, DEPTH]}
          position={[side * (WIDTH / 2 - THICK / 2), height / 2, 0]}
        />
      ))}

      {/* Boards, bottom to top. */}
      {levels.map((y) => (
        <Box key={y} tone="shell" size={[BOARD_WIDTH, THICK, BOARD_DEPTH]} position={[0, y, 0]} />
      ))}

      {/* Rear stabiliser rail — the only thing bridging the open back. */}
      <Box
        tone="shellSoft"
        size={[BOARD_WIDTH, 0.07, 0.014]}
        position={[0, height - 0.05, -DEPTH / 2 + 0.009]}
      />

      {/* Two dressed bays: binders low, books mid. */}
      <group position={[-0.17, 0, -0.02]}>
        <BinderRow width={0.44} y={(levels[1] ?? TOE) + 0.009} height={0.27} accent={accent} seed={6} />
      </group>
      <group position={[0.21, 0, -0.02]}>
        <BookStack base={(levels[2] ?? TOE) + 0.009} width={0.22} depth={0.17} accent={accent} seed={3} />
      </group>
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'shelf-open',
  name: 'Estantería abierta',
  category: 'storage',
  footprint: [WIDTH, DEPTH],
  height: TALL,
  accentable: true,
  variants: [
    { id: 'tall', name: '1,6 m' },
    { id: 'short', name: '1,1 m' },
  ],
  keywords: [
    'estantería',
    'estante',
    'librería',
    'balda',
    'almacenaje',
    'shelf',
    'shelving',
    'bookcase',
    'open storage',
  ],
  Component: ShelfOpen,
}
